import { isFunction } from 'lodash';

import { ShipmentMilestone } from '@/lib/models/shipment/types';
import { formatDateTimeForUser } from '@/utils/format/date-time';
import { tv } from '@/utils/styles';

import { getDaysBetween } from '../utils';

export interface TimelineMilestoneProps {
  milestone: ShipmentMilestone;
  gridSize: number;
  startDate: Date;
  latestMilestone: string;
  isLast?: boolean;
  activeMilestone?: boolean;
  onActive?: (state: boolean) => void;
}

const TimelineBarMilestone = ({
  milestone,
  gridSize,
  startDate,
  latestMilestone,
  isLast,
  activeMilestone,
  onActive,
}: TimelineMilestoneProps) => {
  const { date, label, completed, id, locationCode } = milestone;
  const isPrimary = latestMilestone === id;
  const labelIsVisible = !activeMilestone && isPrimary;
  const daysOffset = getDaysBetween(startDate, new Date(date), false);

  const { base, marker, milestoneLabel, milestoneDate } = styles({
    isCompleted: completed,
    isPrimary,
    labelIsVisible,
    isLast,
  });

  const handleMouseEnter = () => {
    if (isFunction(onActive)) onActive(true);
  };

  const handleMouseLeave = () => {
    if (isFunction(onActive)) onActive(false);
  };

  return (
    <div
      className={base()}
      style={{ left: `${daysOffset * gridSize}px` }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className={marker()} />
      <span className={milestoneLabel()}>
        {locationCode} • {label}
        <span className={milestoneDate()}>
          {' '}
          • {!completed ? 'ETA ' : ''}
          {formatDateTimeForUser(date)}
        </span>
      </span>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'group absolute bottom-0 left-0 top-0 z-8 grid -translate-x-1/2 place-items-center hover:z-10',
    marker: 'block size-[5px] rotate-45 rounded-[2px] border outline outline-1 outline-white group-hover:!size-2',
    milestoneLabel:
      'pointer-events-none absolute -bottom-[18px] -left-2 whitespace-nowrap bg-grey-50 pl-1 text-xs opacity-0 transition-all group-hover:opacity-100',
    milestoneDate: 'text-grey-400',
  },
  variants: {
    isCompleted: {
      true: {
        base: 'z-9',
        marker: 'border-solid border-green-200 bg-green-50 outline-1 group-hover:border-green-600',
      },
      false: {
        marker: 'border-dotted border-grey-500 bg-grey-50 group-hover:border-grey-600',
      },
    },
    isPrimary: {
      true: {
        marker: '!size-2 !border-green-600',
      },
      false: {},
    },
    labelIsVisible: {
      true: {
        milestoneLabel: 'opacity-100',
      },
      false: {},
    },
    isLast: {
      true: {
        milestoneLabel: 'left-1/2 -translate-x-1/3',
      },
      false: {},
    },
  },
});

export { TimelineBarMilestone };
